/**
 * Lista plana de procedimiento: prefill desde incidente y utilidades por tabla de subsección.
 *
 * Pareja explícita con `planiwise-app/functions/incidentSubTableProcedureList.ts`: mismo comportamiento
 * en ambos repos. Las definiciones (`subTableKind`, columnas, keyPointers) viven en
 * `incidentSubTable.ts`, archivo triplicado (stem-web-backend, planiwise-frontend, planiwise-app);
 * un cambio allí debe replicarse en las tres rutas antes de asumir paridad API ↔ UI ↔ móvil.
 */

import {
  applyIncidentSubTableRowPrefillToPregunta,
  getIncidentSubTableDef,
  isIncidentSubTableProcedureSubsectionOfKind,
  listIncidentSubTableDefs,
  resolveIncidentSubTableSubseccionForPreguntaIndex,
  type IncidentSubTableDef,
} from 'src/modules/incident/incidentSubTable';

/** Reasigna listIndex (solo nodos que no son fila del procedimiento) y subsectionIndex en preguntas. */
export function renumberProcedureListaIndexes(list: any[]): void {
  if (!Array.isArray(list)) return;
  list.forEach((item, idx) => {
    if (!item || typeof item !== 'object') return;
    if (item.tipo === 'subseccion' || item.tipo === 'pregunta') return;
    item.listIndex = idx;
  });
  let currentSubIdx: number | null = null;
  for (let i = 0; i < list.length; i++) {
    const item = list[i];
    if (item?.tipo === 'subseccion') {
      currentSubIdx = i;
    } else if (item?.tipo === 'pregunta' && currentSubIdx !== null) {
      item.subsectionIndex = currentSubIdx;
    } else if (item?.tipo === 'seccion') {
      currentSubIdx = null;
    }
  }
}

function findFirstSubTableBlockRange(
  list: any[],
  subTableKind: string,
): {start: number; end: number} | null {
  if (!Array.isArray(list)) return null;
  const start = list.findIndex((item) =>
    isIncidentSubTableProcedureSubsectionOfKind(item, subTableKind),
  );
  if (start === -1) return null;
  let end = start + 1;
  while (end < list.length && list[end]?.tipo === 'pregunta') {
    end++;
  }
  return {start, end};
}

function findLastSubTableDynamicBlockRange(
  list: any[],
  subTableKind: string,
): {start: number; end: number} | null {
  if (!Array.isArray(list)) return null;
  let last: {start: number; end: number} | null = null;
  for (let i = 0; i < list.length; i++) {
    if (isIncidentSubTableProcedureSubsectionOfKind(list[i], subTableKind)) {
      let j = i + 1;
      while (j < list.length && list[j]?.tipo === 'pregunta') {
        j++;
      }
      last = {start: i, end: j};
      i = j - 1;
    }
  }
  return last;
}

function assignIncidentSubTableDynamicIndicesInPlace(list: any[]): void {
  const perKind = new Map<string, number>();
  for (const item of list) {
    if (item?.tipo !== 'subseccion' || !item.isDynamic) continue;
    const k = String(item.subTableKind || '');
    if (!getIncidentSubTableDef(k)) continue;
    const n = perKind.get(k) ?? 0;
    item.dynamicIndex = n;
    perKind.set(k, n + 1);
  }
}

function countSubTableDynamicBlocks(list: any[], subTableKind: string): number {
  if (!Array.isArray(list)) return 0;
  return list.filter((item) => isIncidentSubTableProcedureSubsectionOfKind(item, subTableKind)).length;
}

function cloneSubTableBlock(block: any[]): any[] {
  return block.map((raw) => {
    const copy = {...raw};
    if (Array.isArray(copy.additionalPhotos)) {
      copy.additionalPhotos = copy.additionalPhotos.map((p: any) => ({...p}));
    }
    return copy;
  });
}

/**
 * Garantiza al menos tantas subsecciones dinámicas del `subTableKind` dados como filas en
 * `incidentResponse[def.incidentRowsPath]` (mínimo 1 bloque vacío por plantilla encontrada).
 */
export function ensureSubTableDynamicBlockCount(
  list: any[],
  incidentResponse: any,
  subTableKind: string,
  incidentRowsPath: string,
): any[] {
  if (!Array.isArray(list) || !list.length) return list;
  const rows = incidentResponse?.[incidentRowsPath];
  const target = Array.isArray(rows) && rows.length > 0 ? rows.length : 1;
  const range = findFirstSubTableBlockRange(list, subTableKind);
  if (!range) return list;

  const templateBlock = list.slice(range.start, range.end);
  let out = [...list];

  while (countSubTableDynamicBlocks(out, subTableKind) < target) {
    const lastR = findLastSubTableDynamicBlockRange(out, subTableKind);
    if (!lastR) break;
    const clone = cloneSubTableBlock(templateBlock);
    out = [...out.slice(0, lastR.end), ...clone, ...out.slice(lastR.end)];
    renumberProcedureListaIndexes(out);
  }
  return out;
}

function incidentSubTableDefSupportsPrefill(def: IncidentSubTableDef): boolean {
  if (def.applyIncidentRowToProcedureQuestion) return true;
  return def.columnTemplates.some((t) => t.rowField);
}

function prefillSubTableRowsIntoProcedureList(
  list: any[],
  incidentResponse: any,
  def: IncidentSubTableDef,
): any[] {
  if (!incidentSubTableDefSupportsPrefill(def)) return list;
  if (!Array.isArray(list) || !list.length || !incidentResponse) return list;
  const rows = Array.isArray(incidentResponse[def.incidentRowsPath])
    ? incidentResponse[def.incidentRowsPath]
    : [];
  if (!rows.length) return list;

  return list.map((item, i) => {
    if (item?.tipo !== 'pregunta' || !item.keyPointer) return item;
    const sub = resolveIncidentSubTableSubseccionForPreguntaIndex(list, i, def);
    if (!sub || !isIncidentSubTableProcedureSubsectionOfKind(sub, def.subTableKind)) return item;
    const row = rows[Number(sub.dynamicIndex) || 0];
    if (!row) return item;

    const next = {...item};
    applyIncidentSubTableRowPrefillToPregunta(next, String(next.keyPointer), row, def);
    return next;
  });
}

function prefillSubTableRowsForDef(
  list: any[],
  incidentResponse: any,
  def: IncidentSubTableDef,
): any[] {
  return prefillSubTableRowsIntoProcedureList(list, incidentResponse, def);
}

export function ensureAndPrefillIncidentSubTableProcedureList(list: any[], incidentResponse: any): any[] {
  if (!Array.isArray(list) || !list.length) return list;
  if (!incidentResponse) return list;
  let next = list.map((x) => ({...x}));
  for (const def of listIncidentSubTableDefs()) {
    next = ensureSubTableDynamicBlockCount(
      next,
      incidentResponse,
      def.subTableKind,
      def.incidentRowsPath,
    );
  }
  assignIncidentSubTableDynamicIndicesInPlace(next);
  for (const def of listIncidentSubTableDefs()) {
    next = prefillSubTableRowsForDef(next, incidentResponse, def);
  }
  return next;
}
